// MainTitle.tsx

import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import * as motion from "motion/react-client";
import "./MainTitle.scss";

import { useSlice } from "@bridge-applications/provider-kit";
// import Skeleton from "../../../../common/Skeleton/Skeleton";
import { useTranslation } from "react-i18next";

export default function MainTitle() {
    const { state: appState } = useSlice("appSlice");
    const { t } = useTranslation();

    /** ref to the real <svg> element rendered by the React-imported component */
    const svgRef = useRef<SVGSVGElement | null>(null);

    useLayoutEffect(() => {
        if (appState.loading?.appSession) return;
        const svg = svgRef.current;
        if (!svg) return;

        // Get the SVG's bounding box for proper dimensions
        const bbox = svg.getBBox();
        const height = bbox.height || 74; // fallback to your SVG height

        /* ------------------------------------------------------------------ */
        /* 1.  Create a mask to clip the shine to the logo shape             */
        /* ------------------------------------------------------------------ */
        let mask = svg.querySelector("#shineMask") as SVGMaskElement | null;
        if (!mask) {
            const NS = "http://www.w3.org/2000/svg";

            // Create mask that uses the logo paths
            mask = document.createElementNS(NS, "mask");
            mask.setAttribute("id", "shineMask");

            // Copy all paths into the mask (white = visible)
            const paths = svg.querySelectorAll("path");
            paths.forEach((path) => {
                const maskPath = path.cloneNode(true) as SVGPathElement;
                maskPath.setAttribute("fill", "white");
                mask!.appendChild(maskPath);
            });

            let defs = svg.querySelector("defs");
            if (!defs) {
                defs = document.createElementNS(NS, "defs");
                svg.prepend(defs);
            }
            defs.appendChild(mask);
        }

        /* ------------------------------------------------------------------ */
        /* 2.  Create blur filter for the shine                              */
        /* ------------------------------------------------------------------ */
        let blurFilter = svg.querySelector(
            "#shineBlur",
        ) as SVGFilterElement | null;
        if (!blurFilter) {
            const NS = "http://www.w3.org/2000/svg";
            blurFilter = document.createElementNS(NS, "filter");
            blurFilter.setAttribute("id", "shineBlur");

            const blur = document.createElementNS(NS, "feGaussianBlur");
            blur.setAttribute("stdDeviation", "2.1");
            blurFilter.appendChild(blur);

            let defs = svg.querySelector("defs");
            if (!defs) {
                defs = document.createElementNS(NS, "defs");
                svg.prepend(defs);
            }
            defs.appendChild(blurFilter);
        }

        /* ------------------------------------------------------------------ */
        /* 3.  Create the shine rectangle with gradient                       */
        /* ------------------------------------------------------------------ */
        let shineRect = svg.querySelector(
            "#shineRect",
        ) as SVGRectElement | null;
        let grad = svg.querySelector(
            "#shineGradient",
        ) as SVGLinearGradientElement | null;

        if (!grad) {
            const NS = "http://www.w3.org/2000/svg";

            // Create gradient for the shine band
            grad = document.createElementNS(NS, "linearGradient");
            grad.setAttribute("id", "shineGradient");
            grad.setAttribute("gradientUnits", "userSpaceOnUse");
            grad.setAttribute("x1", "0");
            grad.setAttribute("y1", "0");
            grad.setAttribute("x2", "0");
            grad.setAttribute("y2", height.toString());

            // Create a narrow band of white
            const stops: [string, string, string][] = [
                ["0%", "transparent", "0"],
                ["40%", "transparent", "0"],
                ["48%", "rgba(255,255,255,0.8)", "1"],
                ["50%", "rgba(255,255,255,1)", "1"],
                ["52%", "rgba(255,255,255,0.8)", "1"],
                ["60%", "transparent", "0"],
                ["100%", "transparent", "0"],
            ];

            stops.forEach(([off, col, op]) => {
                const s = document.createElementNS(NS, "stop");
                s.setAttribute("offset", off);
                s.setAttribute("stop-color", col);
                if (op) s.setAttribute("stop-opacity", op);
                grad!.appendChild(s);
            });

            let defs = svg.querySelector("defs");
            if (!defs) {
                defs = document.createElementNS(NS, "defs");
                svg.prepend(defs);
            }
            defs.appendChild(grad);
        }

        if (!shineRect) {
            const NS = "http://www.w3.org/2000/svg";

            // Create a rectangle that covers the entire SVG
            shineRect = document.createElementNS(NS, "rect");
            shineRect.setAttribute("id", "shineRect");
            shineRect.setAttribute("x", bbox.x.toString());
            shineRect.setAttribute("y", bbox.y.toString());
            shineRect.setAttribute("width", bbox.width.toString());
            shineRect.setAttribute("height", bbox.height.toString());
            shineRect.setAttribute("fill", "url(#shineGradient)");
            shineRect.setAttribute("mask", "url(#shineMask)");
            shineRect.setAttribute("filter", "url(#shineBlur)");
            (shineRect as unknown as HTMLElement).style.mixBlendMode = "screen";
            (shineRect as unknown as HTMLElement).style.pointerEvents = "none";

            svg.appendChild(shineRect);
        }

        /* ------------------------------------------------------------------ */
        /* 4.  Animate the gradient position from bottom to top               */
        /* ------------------------------------------------------------------ */
        const ctx = gsap.context(() => {
            // Start with the shine below the logo and move it up through
            gsap.fromTo(
                grad,
                {
                    attr: {
                        y1: height * 0.5,
                        y2: height * 1.5,
                    },
                },
                {
                    attr: {
                        y1: -height * 0.5,
                        y2: height * 0.5,
                    },
                    duration: 1.5,
                    ease: "power2.inOut",
                    repeat: -1,
                    repeatDelay: 0.65,
                },
            );
        }, svg);

        return () => ctx.revert();
    }, [appState.loading?.appSession]);
    return (
        <div className="main-title">
            {/* {appState.loading?.appSession ? (
                <Skeleton
                    baseColor="#000000"
                    highlightVariation={35}
                    borderRadius={"5vw"}
                    height={"8.68svh"}
                    width={"57.5vw"}
                    opacity={0.25}
                />
            ) : (
                <> */}
            {/* <Title ref={svgRef} className="main-title__title" />
                    <h3 className="main-title__desc">{t("MainTitle.desc")}</h3> */}

            <motion.div
                className="main-title__block"
                animate={{
                    rotate: [0, 0, 180, 180, 0],
                    borderRadius: ["0%", "0%", "50%", "50%", "0%"],
                }}
                transition={{
                    duration: 2,
                    ease: "easeInOut",
                    times: [0, 0.2, 0.5, 0.8, 1],
                    repeat: Infinity,
                    repeatDelay: 10,
                }}
            />

            <h1 className="main-title__title">{t("MainTitle.earnYourWay")}</h1>
            {/* </>
            )} */}
        </div>
    );
}
